import React, { useEffect, useState } from 'react';
import {
  Container,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TablePagination,
  Snackbar,
  Alert,
  Paper,
  Tooltip,
  CircularProgress,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import SecurityIcon from '@mui/icons-material/Security';
import axios from 'axios';
import { API_BASE_URL } from '../config';

export default function BoletoList({ usuario, senha, authHeader, senhaDestaque, empresa }) {
  const [boletos, setBoletos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const cores = empresa?.cores || { primary: '#1976d2', secondary: '#1565c0' };

  function isVencido(boleto) {
    const hoje = new Date();
    const [dia, mes, ano] = boleto.VENCIMENTO.split('/');
    const vencimentoDate = new Date(`${ano}-${mes}-${dia}T00:00:00`);

    return vencimentoDate < hoje && boleto.PAGO !== 'S';
  }

  function formatCurrency(value) {
    if (!value) return '';
    const num = Number(value.replace('.', '').replace(',', '.'));
    if (isNaN(num)) return '';
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const totalBoletos = boletos.length;
  const boletosPagos = boletos.filter((boleto) => boleto.PAGO === 'S').length;
  const boletosVencidos = boletos.filter((boleto) => isVencido(boleto) && boleto.PAGO !== 'S').length;
  const boletosEmAberto = totalBoletos - boletosPagos - boletosVencidos;

  const getStatusChip = (boleto) => {
    if (boleto.PAGO === 'S') {
      return <Chip label="Pago" size="small" color="success" />;
    }
    if (isVencido(boleto)) {
      return <Chip label="Vencido" size="small" color="error" />;
    }
    return <Chip label="Em aberto" size="small" color="warning" />;
  };

  const copyToClipboard = (text, message) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        showSnackbar(message);
      })
      .catch(() => {
        showSnackbar('Falha ao copiar para a área de transferência.', 'error');
      });
  };

  const handleDownloadBoleto = async (boleto) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/rota/boleto/download/${boleto.ID}`,
        {},
        {
          headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `boleto_${boleto.ID}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      showSnackbar('Download iniciado com sucesso!');
    } catch (err) {
      console.error('Erro ao baixar boleto:', err);
      showSnackbar('Erro ao baixar o boleto.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    async function fetchBoletos() {
      setLoading(true);
      setError(null);
      try {
        // Remove a formatação do CNPJ (mantém apenas números)
        const cnpjLimpo = empresa.cnpj ? empresa.cnpj.replace(/\D/g, '') : '';
        
        const response = await axios.post(
          `${API_BASE_URL}/rota/boleto/lista`,
          { 
            usuario: usuario.replace(/\D/g, ''), 
            senha: senha, 
            empresa: cnpjLimpo // Agora envia o CNPJ da empresa
          },
          { headers: { Authorization: authHeader, 'Content-Type': 'application/json' } }
        );
        setBoletos(response.data);
      } catch (err) {
        setError('Erro ao carregar boletos.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    if (usuario && authHeader && empresa) {
      fetchBoletos();
    }
  }, [usuario, senha, authHeader, empresa]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Container sx={{ mt: 12 }}>
        <Paper elevation={3} sx={{ p: 4, backgroundColor: '#fff4f4' }}>
          <Typography variant="h6" color="error" sx={{ mb: 1 }}>
            Erro
          </Typography>
          <Typography>{error}</Typography>
        </Paper>
      </Container>
    );

  if (boletos.length === 0)
    return (
      <Container sx={{ mt: 12 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Nenhum boleto encontrado
          </Typography>
          <Typography color="text.secondary">
            Não há boletos registrados para este usuário no momento. Tente novamente mais tarde.
          </Typography>
        </Paper>
      </Container>
    );

  return (
    <Container sx={{ mt: 12, pb: 6 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Boletos em aberto
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Consulte e baixe seus boletos com segurança.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
          <Chip label={`Total ${totalBoletos}`} size="small" variant="outlined" />
          <Chip label={`Pagos ${boletosPagos}`} size="small" color="success" variant="outlined" />
          <Chip label={`Vencidos ${boletosVencidos}`} size="small" color="error" variant="outlined" />
          <Chip label={`Abertos ${boletosEmAberto}`} size="small" color="warning" variant="outlined" />
        </Stack>
      </Box>

      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 3, border: `1px solid ${cores.primary}`, backgroundColor: 'rgba(248, 249, 250, 0.9)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon sx={{ color: cores.primary, fontSize: 24 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Verifique antes de pagar
          </Typography>
        </Box>
        <Typography color="text.secondary" sx={{ mt: 1, fontSize: 14 }}>
          Confirme beneficiário, valor e data de vencimento antes de efetuar o pagamento.
        </Typography>
      </Paper>

      {senhaDestaque && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
          A senha informada é a padrão. Altere a senha usando o botão "Senha" no topo da página para proteger suas informações.
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 16px 40px rgba(15, 23, 42, 0.08)' }}>
        <Table>
          <TableHead sx={{ backgroundColor: cores.primary }}>
            <TableRow>
              <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Vencimento</TableCell>
              <TableCell align="right" sx={{ color: '#fff', fontWeight: 700 }}>
                Valor
              </TableCell>
              <TableCell align="center" sx={{ color: '#fff', fontWeight: 700 }}>
                Linha Digitável
              </TableCell>
              <TableCell align="center" sx={{ color: '#fff', fontWeight: 700 }}>
                Download
              </TableCell>
              <TableCell align="center" sx={{ color: '#fff', fontWeight: 700 }}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {boletos
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((boleto) => (
                <TableRow
                  key={boleto.ID}
                  hover
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    backgroundColor:
                      boleto.PAGO === 'S'
                        ? '#f0f9ef'
                        : isVencido(boleto)
                        ? '#fff1f0'
                        : 'inherit',
                  }}
                >
                  <TableCell
                    sx={{
                      color: boleto.PAGO === 'S' ? 'green' : isVencido(boleto) ? 'red' : 'inherit',
                      fontWeight: boleto.PAGO === 'S' ? 'bold' : 'normal',
                    }}
                  >
                    {boleto.VENCIMENTO}
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      color: boleto.PAGO === 'S' ? 'green' : isVencido(boleto) ? 'red' : 'inherit',
                      fontWeight: boleto.PAGO === 'S' ? 'bold' : 'normal',
                    }}
                  >
                    {formatCurrency(boleto.VALOR)}
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title="Copiar linha digitável">
                      <IconButton
                        color="primary"
                        onClick={() => copyToClipboard(boleto.LINHADIGITAVEL, 'Linha digitável copiada!')}
                      >
                        <QrCode2Icon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title="Baixar PDF do boleto">
                      <span>
                        <IconButton
                          color="primary"
                          onClick={() => handleDownloadBoleto(boleto)}
                          disabled={boleto.PAGO === 'S'}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>

                  <TableCell align="center">{getStatusChip(boleto)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={boletos.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{ '& .MuiTablePagination-toolbar': { px: 2 } }}
        />
      </TableContainer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
}
